import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import currency_ from 'currency.js';
import { get, isEqual, isNil, isObject, merge, omit } from 'lodash';
import moment_ from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import {
    DATE_TIME_DEFAULT_FORMAT,
    DEFAULT_LANGUAGE,
    DEFAULT_NUMBER_PRECISION,
    SHORT_DATE_FORMAT_WITH_LOCALE,
    SHORT_TIME_FORMAT_WITH_LOCALE,
    TIME_FORMAT,
} from '@config/global-constants';
import { show_event_selector } from '@rpt-ngrx/actions/events.action';
import { show_site_selector } from '@rpt-ngrx/actions/sites.action';
import { selectIframePostMessageResult as iframeEventPostMessageResult } from '@rpt-ngrx/selectors/events.selector';
import { selectIsIframeMode } from '@rpt-ngrx/selectors/session.selector';
import { selectIframePostMessageResult } from '@rpt-ngrx/selectors/sites.selector';
import { ColumnFieldClass } from '@services/preferences/preferences.enum';
import { EventSelectorOptions, SiteSelectorOptions } from '@shared/types';
import {
    ICompanyCurrecies,
    ICompanyPreferences,
    ICurrencyAndNumbers,
    IInventoryCurrencyAndNumbers,
} from '@shared/types/company/company-preferences.type';
import { CompanyUserRole } from '@shared/types/companyUserRole';
import { IDayPartOptions } from '@shared/types/report';
import {
    convertUTCDateTimeByTimeZone,
    formatUTCDateTimeWithoutTimeZone,
    generateId,
    removeInvalidZeros,
    removeNegativeLabelForPdf,
} from '@shared/utils';

import { ErrorHandlerService } from '../helper';
import { CustomHttpClient } from '../http-client';

const moment = moment_;
const currency = currency_;

interface ICurrencyOptions {
    symbol?: string;
    separator: string;
    decimal?: string;
    precision: number;
    pattern?: string;
    negativePattern?: string;
}

@Injectable({
    providedIn: 'root',
})
export class PreferencesService {
    private _currencyAndNumbersPreferences: ICompanyPreferences = {
        currencyAndNumbers: {
            currency: 'USD',
            decimalSeparator: '.',
            negatives: '(1.1)',
            precision: DEFAULT_NUMBER_PRECISION,
            roundingMethod: 'nearest',
            symbol: '$',
            symbolPositioning: 'left',
            thousandsSeparator: ',',
        },
        percentageOptions: {
            symbol: '%',
            symbolPositioning: 'right',
        },
        dateAndTime: {
            locale: 'da',
            timezone: '',
        },
        allCurrencies: [
            {
                code: 'USD',
                description: 'Currency scheme',
                entity_id: '58f57e11b577d78d1a7da4fe',
                name: 'US',
                precision: DEFAULT_NUMBER_PRECISION,
                symbol: '$',
                symbol_positioning: 'left',
                _id: '60a298a152d238b04de78165',
            },
        ],
        id: '',
        name: 'None',
        preferred_language: 'en-US',
        eod: '',
        hasPreTenderVoidAmounts: false,
        hasLiabilitySalesIntoGrossSales: false,
        hasStartingAmount: false,
        hasActiveEvents: false,
        hasChildCategories: false,
        backOfficeCurrencyAndNumbers: {
            calculationPrecision: 'use_currency_schema',
            quantityPrecision: 6,
        },
        costMethod: 'last_purchase_cost',
        backOffice: {
            transferDisplayQohFromSetting: true,
            standWorksheetPostClosureSetting: true,
        },
        reportGroupingMethod: 'create_time',
    };
    private _siteHierarchyId = null;
    private _dayPartOrderingPreferences: IDayPartOptions[];
    private _userLang = 'en-US';
    private isIframe = false;

    set userLang(lang: string) {
        this._userLang = lang;
    }

    get userLang(): string {
        return this._userLang;
    }

    get inventoryCurrencyAndNumbersPreferences(): IInventoryCurrencyAndNumbers {
        return this?._currencyAndNumbersPreferences?.backOfficeCurrencyAndNumbers;
    }
    get currencyAndNumbersPreferences(): ICurrencyAndNumbers {
        return this?._currencyAndNumbersPreferences?.currencyAndNumbers;
    }

    get percentagePreferences(): Partial<ICurrencyAndNumbers> {
        return this._currencyAndNumbersPreferences.percentageOptions;
    }

    get siteHierarchyId(): string {
        return this._siteHierarchyId;
    }

    get timeLocale(): string {
        return this?._currencyAndNumbersPreferences?.dateAndTime.locale;
    }

    get timezone(): string {
        return this?._currencyAndNumbersPreferences?.dateAndTime.timezone;
    }

    get getAlterCurrencyPreferences(): Array<ICompanyCurrecies> {
        return this._currencyAndNumbersPreferences.allCurrencies;
    }

    get dayPartOrderingPreferences(): IDayPartOptions[] {
        return this._dayPartOrderingPreferences;
    }

    get preferredLanguage(): string {
        if (!this.isIframe) {
            return localStorage.getItem('global/userLocale');
        }
        return (
            this.userLang ||
            localStorage.getItem('global/userLocale') ||
            this._currencyAndNumbersPreferences.preferred_language
        );
    }

    get costMethodPreferences(): string {
        return isEqual(
            get(this._currencyAndNumbersPreferences, 'costMethod', 'last_purchase_cost'),
            'last_purchase_cost',
        )
            ? 'last_purchase'
            : this._currencyAndNumbersPreferences.costMethod;
    }

    get companyHasPreTenderVoidAmounts(): boolean {
        return this._currencyAndNumbersPreferences.hasPreTenderVoidAmounts;
    }

    get companyHasLiabilitySalesIntoGrossSales(): boolean {
        return this._currencyAndNumbersPreferences.hasLiabilitySalesIntoGrossSales;
    }

    get companyHasStartingAmount(): boolean {
        return this._currencyAndNumbersPreferences.hasStartingAmount;
    }

    get companyChildCategoryOption(): boolean {
        return this._currencyAndNumbersPreferences.hasChildCategories;
    }

    get companyHasActiveEvents(): boolean {
        return this._currencyAndNumbersPreferences.hasActiveEvents;
    }

    get companyReportGroupingMethod(): string {
        return this._currencyAndNumbersPreferences.reportGroupingMethod;
    }

    get percentPrecision(): number {
        return 2;
    }

    constructor(
        private errorHandlerService: ErrorHandlerService,
        private http: CustomHttpClient,
        private store: Store,
        private translations: TranslateService,
    ) {
        this.store
            .select(selectIsIframeMode)
            .pipe(take(1))
            .subscribe((isIframeMode) => (this.isIframe = isIframeMode));
    }

    public getCompanyPreferences(company): Observable<any> {
        const headers = new HttpHeaders({
            'x-company-id': company,
            'x-site-ids': 'All',
        });
        return this.http.get('/preferences/by-company', { headers }).pipe(
            catchError((error: any) => {
                return throwError(this.errorHandlerService.getErrorMessage(error));
            }),
        );
    }

    public getCompanyRoles(companyId: string, userId: string): Observable<{ roles: Array<CompanyUserRole> }> {
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        return this.http
            .get<{ roles: Array<CompanyUserRole> }>(`/preferences/company-roles?user_id=${userId}`, { headers })
            .pipe(
                catchError((error: any) => {
                    return throwError(this.errorHandlerService.getErrorMessage(error));
                }),
            );
    }

    public currencyFormatterForAgGrid = (params, type = 'grid', useXVIPrecision = false): string => {
        const prepareResult = this.prepareCurrency(params);
        if (prepareResult) {
            const { currVal, currencySymbol } = prepareResult;
            const result = useXVIPrecision
                ? this.xviCurrencyFormatter(currVal, currencySymbol)
                : this.formatToCurrency(currVal, currencySymbol);

            if (+currVal < 0 && type !== 'detail') {
                return `<span class="red">${result}</span>`;
            }

            return result;
        }

        return '';
    };

    public customCurrencyFormatterForAgGridWithEmptyValues = (params): string => {
        if ((isObject(params) && params.value === '') || params === '') {
            return '';
        }
        return this.currencyFormatterForAgGrid(params);
    };

    public prepareCurrency(params): { currVal: string; currencySymbol: string } {
        let currVal;
        let currencySymbol = null;

        if (isObject(params) && params.value === undefined) {
            return;
        }
        if (isObject(params) && params.currencySymbol) {
            currencySymbol = params.currencySymbol;
        }
        if (params && (params.value || params.value === null)) {
            currVal = params.value !== null ? params.value : '0';
        } else {
            currVal = !isObject(params) ? (params !== null ? params : '0') : '0';
        }

        if (isNaN(+currVal)) {
            currVal = '0';
        }

        return { currVal, currencySymbol };
    }

    public prepareBaseCurrencyOptions(currency_symbol: string, useXVIPrecision: boolean): ICurrencyAndNumbers {
        const inventoryCurrencyPrecision = this.inventoryCurrencyAndNumbersPreferences.calculationPrecision;
        let preferences: ICurrencyAndNumbers = {
            ...this.currencyAndNumbersPreferences,
            ...(useXVIPrecision
                ? {
                      precision: isNaN(Number(inventoryCurrencyPrecision))
                          ? this.currencyAndNumbersPreferences.precision
                          : Number(this.inventoryCurrencyAndNumbersPreferences.calculationPrecision),
                  }
                : {}),
        };
        if (currency_symbol) {
            const specificCurrencies = this.getAlterCurrencyPreferences;
            const selectedCurrency = specificCurrencies.find((item) => item.symbol === currency_symbol);
            preferences = {
                ...preferences,
                currency: selectedCurrency.code,
                precision: selectedCurrency.precision,
                symbol: selectedCurrency.symbol,
                symbolPositioning: selectedCurrency.symbol_positioning,
            };
        }
        return preferences;
    }

    public currencyFormatterForSalesDetail = (value, currency_symbol): string => {
        if (isNaN(+value)) {
            value = '0';
        }

        if (value < 0) {
            return `<span class="red">${this.formatToCurrency(value, currency_symbol)}</span>`;
        }

        return this.formatToCurrency(value, currency_symbol);
    };

    public currencyFormatterIgnoreNullForAgGrid = (params): string => {
        if (params?.value === null || params === null || params === '') {
            return '';
        }

        return this.currencyFormatterForAgGrid(params);
    };

    public stringCellFormatter = (renderParams, i18nKey = '', translations: TranslateService): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value === undefined || params.value === null) {
            return '';
        }

        if (typeof params.value !== 'string') {
            console.error(
                `Type error: Data for '${params.colDef.headerName}' column in '${params.colDef.field}' field is a ${typeof params.value} but not a string`,
            );
            return '';
        }

        switch (params.value.trim()) {
            case '(None)':
            case 'None':
                return translations.instant('NONE');
            case 'N/A':
                return translations.instant('N/A');
            default:
                return params.value.trim() ? this.getTranslation(i18nKey, params.value) : '';
        }
    };

    public booleanStringCellFormatter = (renderParams, translations: TranslateService): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value === undefined || params.value === null) {
            return '';
        }
        switch (params.value) {
            case '(None)':
            case 'None':
                return translations.instant('NONE');
            case 'N/A':
                return translations.instant('N/A');
            case true:
            case 'true':
                return translations.instant('Yes');
            case false:
            case 'false':
                return translations.instant('No');
            default:
                return params.value;
        }
    };

    public customerCellFormatter = (params, translations: TranslateService): string => {
        let customerName;
        if (typeof params === 'string') {
            return params.length ? translations.instant(params) : '';
        }
        if (typeof params.value === 'string') {
            return params.value.length ? translations.instant(params.value) : '';
        }
        if (params.data && params.data.customer__first_name === '(None)') {
            customerName =
                params.data.customer__last_name === '(None)'
                    ? translations.instant('NONE')
                    : params.data.customer__last_name;
        } else if (params.data) {
            customerName = params.data.customer__first_name;
            customerName =
                params.data.customer__last_name === '(None)'
                    ? customerName
                    : `${customerName} ${params.data.customer__last_name}`;
        } else {
            return '';
        }
        return customerName;
    };

    public stringFormatterForAgGrid = (renderParams): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }
        if (params.node?.id?.startsWith('rowGroupFooter')) {
            const totalForLabel = this.translations.instant('TOTAL_FOR');
            return totalForLabel + ' ' + this.translations.instant(params.value);
        }
        return params.value;
    };

    public numberFormatterForAgGrid = (renderParams, precision = null, type = 'grid'): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }

        if (params.value < 0 && type !== 'detail') {
            return `<span class="red">${this.formatToNumber(params.value)}</span>`;
        }
        return this.formatToNumber(params.value, precision);
    };

    public xcatDaysPastDueNumberFormatterForAgGrid = (renderParams): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }

        return this.formatToNumber(params.value);
    };

    public floatFormatterForAgGrid = (
        renderParams,
        preserveDecimalPrecision: number = null,
        useXVIPrecision = false,
        type = 'grid',
    ): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }

        if (params.value < 0 && type !== 'detail') {
            return `<span class="red">${useXVIPrecision ? this.xviFormatToFloatNumber(params) : this.formatToFloatNumber(params, preserveDecimalPrecision)}</span>`;
        }

        return useXVIPrecision
            ? this.xviFormatToFloatNumber(params)
            : this.formatToFloatNumber(params, preserveDecimalPrecision);
    };

    public dateTimeFormatterForAgGrid = (renderParams, format = `L  ${TIME_FORMAT}`, withTimeZone = false) => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }
        return withTimeZone
            ? convertUTCDateTimeByTimeZone(params.value, this.timezone, format, this.timeLocale)
            : formatUTCDateTimeWithoutTimeZone(params.value, format, this.timeLocale);
    };

    public xviFloatFormatterWithOriginalFormat = (renderParams): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params.value !== 0 && !params.value) {
            return '';
        }
        const options: ICurrencyOptions = omit(
            this.defineFloatingNumberOptions(this.currencyAndNumbersPreferences),
            'negativePattern',
        );
        options.precision = this.inventoryCurrencyAndNumbersPreferences.quantityPrecision;
        const result = currency(
            this.correctRounding(params, this.inventoryCurrencyAndNumbersPreferences.quantityPrecision),
            { ...options },
        ).format();
        const isGreaterThanZero: boolean = params.value > 0;

        return `${isGreaterThanZero ? '+' : ''}${removeInvalidZeros(result, this.currencyAndNumbersPreferences.decimalSeparator, 0)}`;
    };

    public xviFloatFormatterWithArrowForAgGrid = (renderParams): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        let iconTemplate = '';
        if (params.value && Number(params.value) !== 0) {
            iconTemplate = `<i class="red mt-1 ml-5 fa fa-arrow-${params.value < 0 ? 'down' : 'up'}"></i>`;
        }

        return `${this.xviFloatFormatterWithOriginalFormat(params)}${iconTemplate}`;
    };

    public xviCurrencyFormatterWithArrowForAgGrid = (renderParams): string => {
        const prepareResult = this.prepareCurrency(renderParams);
        if (prepareResult) {
            const { currVal } = prepareResult;
            let iconTemplate = '';
            if (currVal && Number(currVal) !== 0) {
                iconTemplate = `<i class="red mt-1 ml-5 fa fa-arrow-${Number(currVal) < 0 ? 'down' : 'up'}"></i>`;
            }
            const result = this.xviCurrencyFormatterWithOriginalFormat(renderParams);

            return `${result}${iconTemplate}`;
        }

        return;
    };

    public xviCurrencyFormatterWithOriginalFormat = (renderParams): string => {
        const prepareResult = this.prepareCurrency(renderParams);
        if (prepareResult) {
            const { currVal, currencySymbol } = prepareResult;
            const preferences = this.prepareBaseCurrencyOptions(currencySymbol, true);
            const options: ICurrencyOptions = omit(this.defineCurrencyOptions(preferences), 'negativePattern');
            const result = currency(this.correctRounding(currVal, options.precision), options).format();
            const isGreaterThanZero: boolean = Number(currVal) > 0;
            const formattedResult = options.decimal === ':' ? result.replace(':', '') : result;

            return `${isGreaterThanZero ? '+' : ''}${removeInvalidZeros(formattedResult, this.currencyAndNumbersPreferences.decimalSeparator)}`;
        }
        return;
    };

    public xviFloatFormatterForAgGrid = (renderParams, type = 'grid'): string => {
        return this.floatFormatterForAgGrid(
            renderParams,
            this.inventoryCurrencyAndNumbersPreferences.quantityPrecision,
            true,
            type,
        );
    };

    public xviFormatToFloatNumber = (val): string => {
        return removeInvalidZeros(
            String(this.formatToFloatNumber(val, this.inventoryCurrencyAndNumbersPreferences.quantityPrecision)),
            this.currencyAndNumbersPreferences.decimalSeparator,
            0,
        );
    };

    public xviCurrencyFormatterForAgGrid = (renderParams, type: string = 'grid'): string => {
        return this.currencyFormatterForAgGrid(renderParams, type, true);
    };

    public xviCurrencyFormatter = (val: string | number, currency_symbol?: string): string => {
        return removeInvalidZeros(
            this.formatToCurrency(val, currency_symbol, true),
            this.currencyAndNumbersPreferences.decimalSeparator,
        );
    };

    public correctRounding = (value, preserveDecimalPrecision?: number): number => {
        value = isObject(value) ? value.value : value;
        const calculatePrecision =
            preserveDecimalPrecision ?? this.currencyAndNumbersPreferences?.precision ?? DEFAULT_NUMBER_PRECISION;
        const defaultPrecision = this.currencyAndNumbersPreferences?.precision === 0 ? 0 : -calculatePrecision;
        let exp = preserveDecimalPrecision ? -calculatePrecision : defaultPrecision;
        const type = this.defineMathMethodForRounding(this.currencyAndNumbersPreferences?.roundingMethod);
        if (typeof exp === 'undefined' || +exp === 0) {
            return Math[type](value);
        }

        value = Number.parseFloat(value);
        exp = +exp;
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
            return NaN;
        }
        value = value.toString().split('e');
        value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
        value = value.toString().split('e');

        return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
    };

    public setCompanyPreferences(data: ICompanyPreferences) {
        this._currencyAndNumbersPreferences = data;
    }

    public setSiteHierarchyId(hierarchyId: string): void {
        this._siteHierarchyId = hierarchyId;
    }

    public formatToCurrency(val: string | number, currency_symbol?: string, useXVIPrecision?: boolean): string {
        const preferences: ICurrencyAndNumbers = this.prepareBaseCurrencyOptions(currency_symbol, useXVIPrecision);
        const options: ICurrencyOptions = this.defineCurrencyOptions(preferences);
        const result = currency(this.correctRounding(val, options.precision), options).format();

        return options.decimal === ':' ? result.replace(':', '') : result;
    }

    public percentsFormatterForAgGrid = (renderParams, preserveDecimalPrecision = null, type = 'grid'): string => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };

        if (params.value !== 0 && !params.value) {
            return '';
        }

        if (params.value < 0 && type !== 'detail') {
            return `<span class="red">${this.formatToPercentage(params.value, preserveDecimalPrecision)}</span>`;
        }
        return this.formatToPercentage(params.value, preserveDecimalPrecision);
    };

    public formatToNumber(val: string | number, preserveDecimalPrecision = null): string {
        const options: ICurrencyOptions = this?.defineNumberOptions(this.currencyAndNumbersPreferences);
        options.precision = preserveDecimalPrecision ?? options.precision;
        return currency(this?.correctRounding(val), { ...options }).format();
    }

    public formatToPercentage(val, preserveDecimalPrecision = null): string {
        const options: ICurrencyOptions = { ...this.definePercentsOptions(this.percentagePreferences) };
        options.precision = preserveDecimalPrecision ?? options.precision;
        return currency(this.correctRounding(val, preserveDecimalPrecision), { ...options }).format();
    }

    public formatToFloatNumber(val: string | number, preserveDecimalPrecision?: number): string {
        const options: ICurrencyOptions = this.defineFloatingNumberOptions(this.currencyAndNumbersPreferences);
        options.precision = preserveDecimalPrecision ? preserveDecimalPrecision : options.precision;
        return currency(this.correctRounding(val, preserveDecimalPrecision), { ...options }).format();
    }

    public formatToFloatTimeNumber(val: string | number): string {
        return this.formatToFloatNumber(val, 1);
    }

    public formatToDuration = (renderParams, naInsteadZero = true) => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (!params.value && params.value !== 0) {
            return '';
        }
        const format = this.hasDotsInTimeFormat() ? 'HH.mm.ss' : 'HH:mm:ss';
        const milliseconds = +params.value;

        if (milliseconds / 3600000 >= 24) {
            const overtime = milliseconds % 3600000;
            const hoursToAdd = (milliseconds - overtime) / 3600000;
            const timeParts = moment.utc(overtime).format(format).split(':');
            const totalHours = +timeParts[0] + hoursToAdd;

            return `${totalHours.toString()}:${timeParts[1]}:${timeParts[2]}`;
        }

        return (moment(milliseconds).isValid() && !(milliseconds < 0)) || (milliseconds === 0 && !naInsteadZero)
            ? moment.utc(milliseconds).format(format)
            : 'N/A';
    };

    public dateFormatter(
        value,
        translateService: TranslateService,
        format = 'L',
        withTimeZone = true,
        locale?: string,
    ): string {
        if (value === undefined || value === null || value === '') {
            return '';
        } else if (value === '(None)') {
            return translateService.instant('NONE');
        } else {
            return withTimeZone
                ? convertUTCDateTimeByTimeZone(value, this.timezone, format, locale ?? this.timeLocale)
                : formatUTCDateTimeWithoutTimeZone(value, format, locale ?? this.timeLocale);
        }
    }

    public businessDateFormatter(renderParams, translateService: TranslateService): string {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (params && !params.value) {
            return '';
        } else if (params.value === '(None)') {
            return translateService.instant('NONE');
        }
        return moment(params.value).locale(this.timeLocale).format('L');
    }

    public dateAndTimeFormatter(
        renderParams,
        translations: TranslateService,
        formatOptions?: {
            dateFormat?: string;
            timeFormat?: moment_.LongDateFormatKey;
            initialFormat?: string;
            timeLocale?: string;
        },
    ) {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (!params.value) {
            return '';
        } else if (params.value === '(None)') {
            return translations.instant('NONE');
        } else if (params.value === 'N/A') {
            return translations.instant('N/A');
        }

        const defaultFormatOptions = {
            dateFormat: SHORT_DATE_FORMAT_WITH_LOCALE,
            timeFormat: SHORT_TIME_FORMAT_WITH_LOCALE,
            initialFormat: DATE_TIME_DEFAULT_FORMAT,
            timeLocale: this.timeLocale,
        };

        const { dateFormat, timeFormat, initialFormat, timeLocale } = merge({}, defaultFormatOptions, formatOptions);
        const originDateTime = moment(params.value, initialFormat ?? undefined);
        const date = originDateTime.locale(timeLocale).format(dateFormat);
        const time = originDateTime.locale(timeLocale).format(timeFormat);
        return `${date} ${time}`;
    }

    public xviDateAndTimeFormatter(renderParams): string {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (!params.value) {
            return '';
        }

        return isNil(params.value) ? '' : moment(params.value).format('MM/DD/YYYY hh:mm:ss A');
    }

    public timeFormatter(renderParams, translations: TranslateService, format = 'LT') {
        const params = isObject(renderParams) ? renderParams : { value: renderParams };
        if (!params.value) {
            return '';
        } else if (params.value === '(None)') {
            return translations.instant('NONE');
        } else if (params.value === 'N/A') {
            return translations.instant('N/A');
        }
        return `${moment(params.value)
            .locale(this.timeLocale)
            .format(this.correctMomentTimeFormat(format as any))}`;
    }

    public hoursFormatter(hours: number | string) {
        if (isNil(hours) || hours === '') {
            return '';
        }
        const hoursInSecond = +hours * 3600;
        return moment().startOf('day').add(hoursInSecond, 'seconds').format('hh:mm A');
    }

    public secondFormatter(seconds: number | string) {
        const format = this.hasDotsInTimeFormat() ? 'HH.MM.SS' : 'HH:MM:SS';
        const formatted = moment
            .utc(+seconds * 1000)
            .locale(this.timeLocale)
            .format(format);
        return formatted;
    }

    public getCorrectLabelForTimeMeasure() {
        const label = this.hasDotsInTimeFormat() ? 'HH.MM.SS' : 'HH:MM:SS';
        return label;
    }

    public hasDotsInTimeFormat() {
        const hasDots = moment.localeData().longDateFormat('LTS').indexOf('.') !== -1;

        return hasDots;
    }

    public correctMomentTimeFormat(format: moment_.LongDateFormatKey) {
        const longDateFormat = moment.localeData().longDateFormat(format);
        return longDateFormat
            ? longDateFormat
                  .replace(/\bh:/g, 'hh:')
                  .replace(/\bh\[e\]/g, 'hh[e]')
                  .replace(/\bH:/g, 'HH:')
                  .replace(/\bH\[e\]/g, 'HH[e]')
            : format;
    }

    public setDayPartOrderingPreferences(options: IDayPartOptions[]) {
        this._dayPartOrderingPreferences = options;
    }

    public sortByDayParts(array: any[], sortByParam: string) {
        if (this.dayPartOrderingPreferences) {
            return array.sort((a, b) => {
                const first = this.dayPartOrderingPreferences.find(
                    (item) => item.day_part_type_entity_id === a.daypart_entity_id,
                );
                const second = this.dayPartOrderingPreferences.find(
                    (item) => item.day_part_type_entity_id === b.daypart_entity_id,
                );
                return first?.priority - second?.priority;
            });
        } else {
            return array.sort((a, b) => {
                if (a[sortByParam] === '(None)') {
                    return 1;
                }
                if (b[sortByParam] === '(None)') {
                    return -1;
                }
                if (a[sortByParam] < b[sortByParam]) {
                    return -1;
                }
                if (a[sortByParam] > b[sortByParam]) {
                    return 1;
                }
                return 0;
            });
        }
    }

    private defineCurrencyOptions(preferences: ICurrencyAndNumbers): ICurrencyOptions {
        const options: ICurrencyOptions = {
            symbol: preferences.symbol,
            separator: preferences.thousandsSeparator,
            decimal: preferences.decimalSeparator === '' ? ':' : preferences.decimalSeparator,
            precision: preferences.precision,
            pattern: this.defineCurrencyPattern(preferences.symbolPositioning),
            negativePattern: this.defineCurrencyNegativePattern(preferences.negatives, preferences.symbolPositioning),
        };

        return options;
    }

    private defineNumberOptions(preferences: ICurrencyAndNumbers): ICurrencyOptions {
        const options: ICurrencyOptions = {
            symbol: '',
            precision: 0,
            separator: preferences.thousandsSeparator,
            negativePattern: this.defineCurrencyNegativePattern(preferences.negatives, preferences.symbolPositioning),
        };

        return options;
    }

    private definePercentsOptions(preferences: Partial<ICurrencyAndNumbers>): ICurrencyOptions {
        const symbol = '%';
        return {
            symbol,
            pattern: this.defineCurrencyPattern(preferences.symbolPositioning),
            precision: this.percentPrecision,
            decimal: preferences.decimalSeparator,
            separator: preferences.thousandsSeparator,
            negativePattern: this.defineCurrencyNegativePattern(preferences.negatives, 'right'),
        };
    }

    private defineFloatingNumberOptions(preferences: ICurrencyAndNumbers): ICurrencyOptions {
        const options: ICurrencyOptions = {
            symbol: '',
            precision: preferences.precision,
            separator: preferences.thousandsSeparator,
            decimal: preferences.decimalSeparator,
            negativePattern: this.defineCurrencyNegativePattern(preferences.negatives, preferences.symbolPositioning),
        };

        return options;
    }

    public getTranslation(prefix: string, key: string): string {
        if (!key) {
            return this.translations.instant('N/A');
        }
        if (key === 'UNKNOWN') {
            return this.translations.instant('UNKNOWN');
        }
        const fullKey = prefix ? `${prefix}.${key.trim()}` : `${key.trim()}`;
        const translated = this.translations.instant(fullKey);
        if (translated === fullKey) {
            return key;
        } else {
            return translated;
        }
    }

    private defineCurrencyPattern(symbolPositioning: string = 'left'): string {
        let pattern: string;
        switch (symbolPositioning) {
            case 'left':
                pattern = '!#';
                break;
            case 'right':
                pattern = '#!';
                break;
            case 'separator':
                pattern = '!#';
                break;
            default:
                pattern = '!#';
        }

        return pattern;
    }

    private defineCurrencyNegativePattern(negatives: string, symbolPositioning: string = 'left'): string {
        let negativePattern: string;

        if (negatives.indexOf('(') !== -1) {
            negativePattern = symbolPositioning === 'left' ? '(!#)' : symbolPositioning === 'right' ? '(#!)' : '(!#)';
        } else if (negatives.indexOf(' ') !== -1) {
            negativePattern = symbolPositioning === 'left' ? '- !#' : symbolPositioning === 'right' ? '- #!' : '- !#';
        } else {
            negativePattern = symbolPositioning === 'left' ? '-!#' : symbolPositioning === 'right' ? '-#!' : '-!#';
        }

        return negativePattern;
    }

    private defineMathMethodForRounding(type: string) {
        switch (type) {
            case 'nearest':
                return 'round';
            case 'roundup':
                return 'ceil';
            case 'rounddown':
                return 'floor';
            default:
                return 'round';
        }
    }

    public async showSiteSelector(
        options: SiteSelectorOptions = {
            required: false,
            multiselect: false,
        },
    ) {
        const msgOptions = { messageId: generateId() };
        const defaultUserLocale = this.preferredLanguage === DEFAULT_LANGUAGE ? null : this.preferredLanguage;

        const optionsWithDefault = {
            userDefinedLocale: defaultUserLocale,
            ...options,
        };
        this.store.dispatch(show_site_selector({ options: optionsWithDefault, msgOptions }));

        const result = await this.postMessageResultAsPromised(msgOptions.messageId);

        if (optionsWithDefault.applySiteSelection) {
            if (!result) {
                window.history.back();
            }
        }
    }

    public async postMessageResultAsPromised(_messageId: string): Promise<any> {
        const result = await this.store.select(selectIframePostMessageResult).pipe().toPromise();

        return result;
    }

    public async showEventSelector(
        options: EventSelectorOptions = {
            required: false,
            multiply: false,
        },
    ) {
        const msgOptions = { messageId: generateId() };
        this.store.dispatch(show_event_selector({ options, msgOptions }));

        const result = await this.postMessageEventPickerResultAsPromised(msgOptions.messageId);
        if (options.applyEventSelection) {
            if (!result) {
                window.history.back();
            }
        }
    }

    public async postMessageEventPickerResultAsPromised(_messageId: string): Promise<any> {
        const result = await this.store.select(iframeEventPostMessageResult).pipe().toPromise();

        return result;
    }

    public getFormatterByClass(cellClass: string): (args) => string {
        switch (true) {
            case cellClass.includes(ColumnFieldClass.CUSTOM_PRICE):
                return this.formatToCurrency.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_PERCENTS):
                return this.formatToPercentage.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_FLOAT):
                return this.formatToFloatNumber.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_TIME):
                return this.formatToFloatTimeNumber.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_DATE):
                return this.dateAndTimeFormatter.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_XVI_FLOAT):
                return this.xviFormatToFloatNumber.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_XVI_PRICE):
                return this.xviCurrencyFormatter.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_XVI_ARROW_FLOAT):
                return this.xviFloatFormatterWithOriginalFormat.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_XVI_ARROW_PRICE):
                return this.xviCurrencyFormatterWithOriginalFormat.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_DURATION):
                return this.formatToDuration.bind(this);
            case cellClass.includes(ColumnFieldClass.CUSTOM_COUNT):
                return this.formatToNumber.bind(this);
        }
    }

    public xviPDFContentValueFormatter(value: string | number, config: Record<string, any>): string | number {
        let content;
        if (isNil(value)) {
            return '';
        }

        if (config?.isCurrency) {
            content = removeNegativeLabelForPdf(`${this.xviCurrencyFormatterForAgGrid(value, 'detail')}`);
        } else if (config?.isFloatNumber) {
            content = removeNegativeLabelForPdf(`${this.xviFloatFormatterForAgGrid(value, 'detail')}`);
        } else {
            content = value;
        }

        return content;
    }
}
